import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  FileText,
  User,
  Hash,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ShieldCheck,
  RefreshCw,
  ExternalLink,
  Send,
  XCircle,
  Globe,
  Mic,
  Tag,
  ChevronDown,
  Archive,
  Loader2,
  Share2,
  Pencil,
  X,
} from 'lucide-react';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/ui/modal.js';
import { SynthesisReleasePanel } from '../components/threads/SynthesisReleasePanel.js';
import { addArticleCorrection } from '../lib/api/articles.js';
import { usePresence } from '../hooks/usePresence.js';
import { PresencePill } from '../components/PresencePill.js';
import { handleMutationConflict } from '../lib/mutationConflict.js';
import { SUBSTANCE_LABEL, substanceBadgeClass } from '../lib/substance.js';
import {
  getArticle,
  markArticleViewed,
  getSourcePdfUrl,
  approveArticle,
  unexpireArticle,
  previewQgFixBatch,
  commitQgFixBatch,
  getArticlePublishTargets,
  getArticlePublishingHistory,
  unpublishArticle,
  publishArticle,
  promoteArticle,
  getArticleRelationships,
  deleteArticleRelationship,
  getRewriteDecisions,
  approveRewriteDecision,
  rejectRewriteDecision,
  getArticleSources,
  overrideSourceRole,
  getArticleReferencedPeriods,
  setMarketFloorSection,
  updateArticleContent,
  MARKETFLOOR_SECTIONS,
  type MarketFloorSection,
} from '../lib/api/index.js';
import {
  updateArticleTitle,
} from '../lib/api/articles.js';
import type {
  RewriteDecision,
  ArticleSource,
  ArticleSourcesResponse,
  ReferencedPeriod,
} from '../lib/api/articles.js';
import type { SocialExposure } from '../lib/api/types.js';
import { RegenerateArticleDialog } from '../components/RegenerateArticleDialog.js';
import { PublishDialog } from '../components/PublishDialog.js';
import { CorrectCompanyDialog } from '../components/CorrectCompanyDialog.js';
import { QualityGatePanel } from '../components/QualityGatePanel.js';
import { QgFixBatchModal } from '../components/QgFixBatchModal.js';
import { ContentHistoryPanel } from '../components/ContentHistoryPanel.js';
import { QualityGateOverrideDialog } from '../components/QualityGateOverrideDialog.js';
import { ArticleBody } from '../components/ArticleBody.js';
import { ArticleImagesPanel } from '../components/articles/ArticleImagesPanel.js';
import { MarkdownEditor } from '../components/MarkdownEditor.js';
import { format } from 'date-fns';

const SYNC_REASON_LABELS: Record<string, (d: Record<string, any>) => string> = {
  new_sibling_cross_target: (d) =>
    `New sibling: "${d.sibling_title ?? 'untitled'}"`,
};

function formatSyncReason(code: string, detail: Record<string, any>): string {
  const fmt = SYNC_REASON_LABELS[code];
  return fmt ? fmt(detail) : code;
}

export function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  // Public corrections disclosure (2026-07-28): operator-authored
  // note appended to the article's change history and republished.
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionNote, setCorrectionNote] = useState('');
  const [correctionKind, setCorrectionKind] = useState<'correction' | 'update'>('correction');
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showCorrectCompanyDialog, setShowCorrectCompanyDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [showUnpublishPicker, setShowUnpublishPicker] = useState(false);
  const [unpublishFeedback, setUnpublishFeedback] = useState<string | null>(null);
  // WP-14: social posts tied to a just-unpublished article that the operator
  // may need to pull manually (posted, or queued in Buffer). Sticky until
  // dismissed — unlike the transient unpublish feedback.
  const [socialExposure, setSocialExposure] = useState<SocialExposure[] | null>(null);
  const [promoteFeedback, setPromoteFeedback] = useState<{ type: 'success' | 'error'; message: string; prUrl?: string } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const unpublishPickerRef = useRef<HTMLDivElement>(null);

  // Close unpublish dropdown on click outside
  useEffect(() => {
    if (!showUnpublishPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (unpublishPickerRef.current && !unpublishPickerRef.current.contains(e.target as Node)) {
        setShowUnpublishPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUnpublishPicker]);

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['article', id],
    queryFn: async () => {
      const articleData = await getArticle(id!);
      // Mark as viewed (fire and forget - don't wait for response)
      markArticleViewed(id!).catch((err) => {
        console.warn('Failed to mark article as viewed:', err);
      });
      return articleData;
    },
    enabled: !!id,
  });

  // Fetch PDF URL for the source document
  const { data: pdfData } = useQuery({
    queryKey: ['article-pdf', id],
    queryFn: () => getSourcePdfUrl(id!),
    enabled: !!id,
  });

  // Publish targets and history
  const { data: publishTargets } = useQuery({
    queryKey: ['article-publish-targets', id],
    queryFn: () => getArticlePublishTargets(id!),
    enabled: !!id,
  });

  const { data: publishHistory } = useQuery({
    queryKey: ['article-publishing-history', id],
    queryFn: () => getArticlePublishingHistory(id!),
    enabled: !!id,
  });

  const { data: relationships } = useQuery({
    queryKey: ['article-relationships', id],
    queryFn: () => getArticleRelationships(id!),
    enabled: !!id,
  });

  // Rewrite-review (mig 114, Step 15 cap): pending operator decisions
  // when an article hit the rewrite cap. Refetched on websocket events
  // so a fresh proposal lands in the panel without manual reload.
  const { data: rewriteDecisions, refetch: refetchRewriteDecisions } = useQuery({
    queryKey: ['article-rewrite-decisions', id],
    queryFn: () => getRewriteDecisions(id!),
    enabled: !!id,
    refetchInterval: 60000,
  });

  // Source bundle (Phase 2): primary + secondaries with doc_form, role,
  // editorial_substance + rewrite history. Renders the Sources-used panel.
  const { data: sourceBundle } = useQuery({
    queryKey: ['article-sources', id],
    queryFn: () => getArticleSources(id!),
    enabled: !!id,
  });

  // Referenced periods (mig 118): regulatory/compliance windows the
  // triggering announcement scoped (ASIC investigations, class-action
  // disclosure periods, etc.). Surfaces the merit decision + back-source
  // status + chart count so operators can see why the article carries
  // the trading context section it does.
  const { data: referencedPeriods } = useQuery({
    queryKey: ['article-referenced-periods', id],
    queryFn: () => getArticleReferencedPeriods(id!),
    enabled: !!id,
  });

  // Publishing job status — tracks in-flight publish/unpublish operations
  const [publishingStatus, setPublishingStatus] = useState<{
    type: 'publishing' | 'unpublishing' | 'published' | 'unpublished' | 'failed';
    target?: string;
    url?: string;
    message?: string;
  } | null>(null);

  // WebSocket: listen for real-time publish events
  // Article-level status changes (publish_status flips) are broadcast on
  // the global `assayerai:articles` channel — same channel ArticlesPage
  // subscribes to. Without this, the detail page only refreshes when its
  // own mutations finish, so an approve/regen/promote done elsewhere
  // (bulk action on the list, auto-regen from QG, another tab) leaves
  // this view showing stale draft state. Match on the current id and
  // invalidate the article query.
  useWebSocket({
    channels: ['assayerai:articles'],
    onEvent: {
      article_status_changed: (data) => {
        if (data.article_id === id) {
          queryClient.invalidateQueries({ queryKey: ['article', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
        }
      },
      // Multi-operator concurrency: content-only mutations (PUT /content,
      // apply-fixes, marketfloor-section) emit this dedicated event so the
      // detail view refreshes when another operator saves. Doesn't
      // invalidate publish-targets / publishing-history (those don't
      // change on content edits — saves a spurious refetch).
      article_content_changed: (data) => {
        if (data.article_id === id) {
          queryClient.invalidateQueries({ queryKey: ['article', id] });
          // Image-related changes (reselect / undo / redo) also flip
          // can_undo / can_redo — keep the action menu accurate.
          queryClient.invalidateQueries({ queryKey: ['article-image-history', id] });
          // The images panel now drives undo/redo off the revision TIMELINE,
          // so another operator's edit has to invalidate that key too or this
          // page keeps offering a "previous revision" that is no longer
          // previous.
          queryClient.invalidateQueries({ queryKey: ['article-image-revisions', id] });
        }
      },
    },
    autoInvalidate: false,
  });

  // Multi-operator presence: announce that this operator has the body
  // editor open. Other operators viewing the article see a "kate editing"
  // chip near the Edit Content button. Beacon ONLY while isEditing is
  // true (passing null makes usePresence a no-op) so passive viewers
  // don't churn presence rows.
  usePresence({
    resourceType: 'article',
    resourceId: isEditing && id ? id : null,
  });

  useWebSocket({
    channels: article?.announcement_id ? [`assayerai:announcement:${article.announcement_id}`] : [],
    onEvent: {
      publish_started: (data) => {
        if (data.article_id === id) {
          setPublishingStatus({ type: 'publishing', target: data.target });
        }
      },
      publish_completed: (data) => {
        if (data.article_id === id) {
          setPublishingStatus({ type: 'published', target: data.target, url: data.externalUrl });
          queryClient.invalidateQueries({ queryKey: ['article', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
          setTimeout(() => setPublishingStatus(null), 8000);
        }
      },
      publish_failed: (data) => {
        if (data.article_id === id) {
          setPublishingStatus({ type: 'failed', target: data.target, message: data.error });
          queryClient.invalidateQueries({ queryKey: ['article', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
          setTimeout(() => setPublishingStatus(null), 10000);
        }
      },
      unpublish_started: (data) => {
        if (data.article_id === id) {
          setPublishingStatus({ type: 'unpublishing', target: data.target });
        }
      },
      unpublish_completed: (data) => {
        if (data.article_id === id) {
          setPublishingStatus({ type: 'unpublished', target: data.target });
          promoteMutation.reset();
          queryClient.invalidateQueries({ queryKey: ['article', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
          setTimeout(() => setPublishingStatus(null), 8000);
        }
      },
      unpublish_failed: (data) => {
        if (data.article_id === id) {
          setPublishingStatus({ type: 'failed', target: data.target, message: data.error });
          queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
          setTimeout(() => setPublishingStatus(null), 10000);
        }
      },
    },
    autoInvalidate: false,
  });

  const approveMutation = useMutation({
    mutationFn: () => approveArticle(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });

  // P8 (2026-10-06): an expired article is read-only until unexpired.
  const unexpireMutation = useMutation({
    mutationFn: () => unexpireArticle(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });

  // Ruling 34 (2026-09-29): an override now publishes on an auto_publish
  // product. A manual-publish product (Orecast activity_item) stops at
  // `approved` by design — say so, rather than leaving the article sitting
  // there as MEM did.
  const [overrideNotPublished, setOverrideNotPublished] = useState<string | null>(null);
  const overrideMutation = useMutation({
    mutationFn: (reason: string) =>
      approveArticle(id!, { override: true, override_reason: reason }),
    onSuccess: (data) => {
      const pub = data?.publish as { queued?: boolean; reason?: string } | undefined;
      setOverrideNotPublished(pub && pub.queued === false ? (pub.reason || 'not queued') : null);
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });

  // Selective QG fix batches (2026-07-30): the operator ticks individual
  // recommendations on the panel; preview runs the copy-edit pass with NO
  // save and opens the review modal; commit persists + records history +
  // stamps the applied issues; discard drops the client-side preview.
  const [fixBatch, setFixBatch] = useState<{
    indexes: number[];
    newContent: string;
    newFaq: Array<{ question: string; answer: string }> | null;
    faqBefore: Array<{ question: string; answer: string }> | null;
    baseUpdatedAt: string;
  } | null>(null);

  const previewFixBatchMutation = useMutation({
    mutationFn: ({ indexes, optionKinds }: { indexes: number[]; optionKinds?: Record<string, string> }) =>
      previewQgFixBatch(id!, indexes, article?.updated_at ?? null, optionKinds),
    onSuccess: (data) => {
      setFixBatch({
        indexes: data.issue_indexes,
        newContent: data.new_content,
        newFaq: data.new_faq ?? null,
        faqBefore: data.faq_before ?? null,
        baseUpdatedAt: data.base_updated_at,
      });
    },
    onError: (e: any) => handleMutationConflict(e, queryClient, [['article', id!]]),
  });

  const commitFixBatchMutation = useMutation({
    mutationFn: () =>
      commitQgFixBatch(id!, {
        new_content: fixBatch!.newContent,
        issue_indexes: fixBatch!.indexes,
        expected_updated_at: fixBatch!.baseUpdatedAt,
        new_faq: fixBatch!.newFaq,
      }),
    onSuccess: () => {
      setFixBatch(null);
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['article-content-history', id] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
    onError: (e: any) => {
      setFixBatch(null);
      handleMutationConflict(e, queryClient, [['article', id!]]);
    },
  });

  // Re-select images — surgical alternative to a full regenerate. Re-runs
  // ONLY the image-pick step (no AI body-write, no PDF re-extraction)
  // against the current scoring engine. Returns a diff so the operator
  // sees what changed (or "no change" if the existing picks already
  // matched the latest engine output).

  // The image-history query that used to live here drove can_undo/can_redo
  // for the dropdown buttons. Those moved to the images panel, which fetches
  // the revision timeline itself — so this page no longer needs it.



  // Save body edit — refactored from inline apiClient.put so onError can
  // route through handleMutationConflict for the 409 race-loser toast.
  // The expected_updated_at lock token comes from the article object
  // that seeded the editor; if another operator saved in between, the
  // backend returns 409 and the manager refetches the latest article.
  const saveContentMutation = useMutation({
    mutationFn: () =>
      updateArticleContent(
        id!,
        { article_content: editContent },
        article?.updated_at ?? null,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      setIsEditing(false);
    },
    onError: (e: any) => {
      if (handleMutationConflict(e, queryClient, [['article', id!]])) {
        // 409 handled — keep editor open so the operator can refresh
        // and re-apply their changes. The toast tells them what happened.
        return;
      }
      alert('Failed to save: ' + (e instanceof Error ? e.message : 'Unknown error'));
    },
  });

  // Save title edit — the published URL never moves (published_slug is
  // frozen at first publish), so a title correction is safe on live
  // articles. D11: saving now republishes to TEST, like an image edit —
  // it used to be save-only, which meant the most visible correction (a
  // wrong headline) was the one that did not ship until somebody remembered
  // a separate Push. Promotion to production stays a separate act, so
  // "Push edits → test" remains for re-pushing without a further edit.
  const saveTitleMutation = useMutation({
    mutationFn: () => updateArticleTitle(id!, titleDraft.trim(), article?.updated_at ?? null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      setIsEditingTitle(false);
    },
    onError: (e: any) => {
      if (handleMutationConflict(e, queryClient, [['article', id!]])) return;
      alert(
        'Failed to save title: '
        + (e?.response?.data?.detail || (e instanceof Error ? e.message : 'Unknown error')),
      );
    },
  });

  const republishMutation = useMutation({
    mutationFn: (params: { targetName: string; destinationName?: string }) =>
      publishArticle(id!, {
        target_name: params.targetName,
        destination_name: params.destinationName,
      }),
    onSuccess: (_data, variables) => {
      setUnpublishFeedback(`Republish job created for ${variables.targetName}. The article will be synced shortly.`);
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
      queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
      setTimeout(() => setUnpublishFeedback(null), 5000);
    },
    onError: (err: any) => {
      setUnpublishFeedback(`Republish failed: ${err?.response?.data?.error || err.message}`);
      setTimeout(() => setUnpublishFeedback(null), 5000);
    },
  });

  // "Push edits → test" — fires `publish_reason='update_existing'` for
  // every unique test target the article currently has. The publishing
  // service overwrites the same slug rather than creating a new one,
  // so the operator can edit a published article and ship the new
  // version without going through unpublish/republish. After this
  // lands, Promote (existing button) pushes the new version to main.
  const updateOnTestMutation = useMutation({
    mutationFn: async (testTargets: Array<{ target_name: string; destination_name?: string }>) => {
      const results = await Promise.allSettled(
        testTargets.map((t) =>
          publishArticle(id!, {
            target_name: t.target_name,
            destination_name: t.destination_name,
            publish_reason: 'update_existing',
          })
        )
      );
      const failures = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      return { queued: testTargets.length - failures.length, failures };
    },
    onSuccess: ({ queued, failures }) => {
      if (failures.length > 0) {
        const sample = failures
          .slice(0, 2)
          .map((f) => (f.reason as any)?.response?.data?.error || (f.reason as any)?.message || 'Unknown error')
          .join('\n');
        setUnpublishFeedback(`Queued ${queued} update(s) on test. ${failures.length} failed:\n${sample}`);
      } else {
        setUnpublishFeedback(
          `Queued ${queued} update(s) on test. The edited content will be live on the test site shortly.`
        );
      }
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
      queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
      setTimeout(() => setUnpublishFeedback(null), 6000);
    },
    onError: (err: any) => {
      setUnpublishFeedback(`Update on test failed: ${err?.response?.data?.error || err.message}`);
      setTimeout(() => setUnpublishFeedback(null), 5000);
    },
  });

  const unpublishMutation = useMutation({
    // WP-14: no targetName = unpublish EVERY live copy (main first, slug
    // retired). targetName scopes to one target's live destinations.
    mutationFn: (params: { targetName?: string }) =>
      unpublishArticle(id!, params.targetName ? { target_name: params.targetName } : {}),
    onSuccess: (data, variables) => {
      const scope = variables.targetName ?? 'all sites';
      const slugNote = data.slug_retired ? ' The published URL has been retired.' : '';
      setUnpublishFeedback(
        `${data.jobs.filter(j => j.job_id).length} unpublish job(s) created for ${scope} — live copies come down first.${slugNote}`
      );
      setSocialExposure(data.social_posts?.length ? data.social_posts : null);
      promoteMutation.reset();
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
      queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
      setTimeout(() => setUnpublishFeedback(null), 8000);
    },
    onError: (err: any) => {
      setUnpublishFeedback(`Unpublish failed: ${err?.response?.data?.error || err.message}`);
      setTimeout(() => setUnpublishFeedback(null), 5000);
    },
  });

  const promoteMutation = useMutation({
    // Snapshot promote — when targetName is omitted the backend fans out to
    // every target where this article has a successful test_branch publish.
    // For single-target articles the call collapses to one PR.
    mutationFn: (params: { targetName?: string }) =>
      promoteArticle(id!, params.targetName),
    onSuccess: (data) => {
      if (data.success) {
        setPromoteFeedback({
          type: 'success',
          message: data.message || 'Promoted to production',
          prUrl: data.pr_url,
        });
        // Refresh publish targets so the UI reflects the new main_branch
        // history rows recorded after a successful snapshot promote.
        queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
      } else {
        setPromoteFeedback({ type: 'error', message: data.message || 'Promotion failed' });
      }
      setTimeout(() => setPromoteFeedback(null), 10000);
    },
    onError: (err: any) => {
      setPromoteFeedback({ type: 'error', message: err?.response?.data?.message || err.message });
      setTimeout(() => setPromoteFeedback(null), 5000);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => import('../lib/api/index.js').then(m => m.archiveArticle(id!)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['article', id] }),
  });

  // Set Market Floor section — one-click move between the 8 MF sections
  // (companies / deals / markets / economy / policy / people / world /
  // opinion). The backend queues a test republish + promote_article so the
  // file ends up at the new section path on both test and main. Only
  // surfaced when the article is published to marketfloor.
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const sectionPickerRef = useRef<HTMLDivElement>(null);
  const sectionMutation = useMutation({
    mutationFn: (section: MarketFloorSection) =>
      setMarketFloorSection(id!, section, article?.updated_at ?? null),
    onSuccess: (data) => {
      setShowSectionPicker(false);
      setPromoteFeedback({
        type: 'success',
        message: data.message || 'Section change queued',
      });
      queryClient.invalidateQueries({ queryKey: ['article', id] });
      queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
      setTimeout(() => setPromoteFeedback(null), 10000);
    },
    onError: (err: any) => {
      // Route 409 through the conflict helper for the dedicated toast +
      // refetch; fall through to the existing in-page banner for other errors.
      if (handleMutationConflict(err, queryClient, [['article', id!]])) {
        setShowSectionPicker(false);
        return;
      }
      setPromoteFeedback({
        type: 'error',
        message: err?.response?.data?.message || err.message || 'Section change failed',
      });
      setTimeout(() => setPromoteFeedback(null), 8000);
    },
  });

  useEffect(() => {
    if (!showSectionPicker) return;
    const onDocClick = (e: MouseEvent) => {
      if (sectionPickerRef.current && !sectionPickerRef.current.contains(e.target as Node)) {
        setShowSectionPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showSectionPicker]);

  const handleRegenerateSuccess = () => {
    // Refresh the article data and articles list
    queryClient.invalidateQueries({ queryKey: ['article', id] });
    queryClient.invalidateQueries({ queryKey: ['articles'] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
          <p className="mt-6 text-grey-600 font-medium">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-16 w-16 text-danger-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-grey-900 mb-2">Article Not Found</h2>
        <p className="text-base text-grey-600 mb-6">
          {error ? (error as Error).message : 'The article could not be found'}
        </p>
        <Link
          to="/articles"
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Articles</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div>
        <Link
          to="/articles"
          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Articles</span>
        </Link>
      </div>

      {/* Rewrite-review proposal (Step 15 cap). Surfaces when the
          planner detected a third-or-later rewrite proposal for this
          article and is waiting on operator approval. Always rendered
          above the action bar so it can't be missed. */}
      {rewriteDecisions && (
        <RewriteReviewPanel
          articleId={id!}
          data={rewriteDecisions}
          onDecided={() => refetchRewriteDecisions()}
        />
      )}

      {/* Action Bar — state-driven primary action + More dropdown */}
      <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-semibold rounded-full ${
              article.publish_status === 'published' ? 'bg-success-100 text-success-700'
              : article.publish_status === 'approved' ? 'bg-primary-100 text-primary-700'
              : article.publish_status === 'archived' ? 'bg-grey-100 text-grey-500'
              : article.publish_status === 'expired' ? 'bg-warning-50 text-warning-800'
              : 'bg-grey-100 text-grey-600'
            }`}>
              {article.publish_status === 'published' ? <CheckCircle className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
              {article.publish_status}
            </span>
            {article.auto_published && (
              <span
                className="inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full bg-grey-200 text-grey-700"
                title="Published automatically — no operator action"
              >
                auto
              </span>
            )}
            {article.quality_score != null && (
              <span className="text-sm text-grey-500">Quality: {(article.quality_score * 100).toFixed(0)}%</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* "Push edits → test" — only when the article has been edited
                since its last publish AND it has at least one test publish.
                Fires `update_existing` so the publisher overwrites the
                same slug rather than creating a duplicate. Independent of
                the draft/approved/published primary button so the
                operator can ship edits without first cycling state. */}
            {article.edited && (() => {
              const testTargets = (publishTargets || [])
                .filter((pt) => pt.destination_type === 'test_branch')
                .map((pt) => ({
                  target_name: pt.target_name,
                  destination_name: pt.destination_name || undefined,
                }));
              if (testTargets.length === 0) return null;
              const label = testTargets.length > 1
                ? `Push edits → test (${testTargets.length} sites)`
                : 'Push edits → test';
              return (
                <button
                  onClick={() => updateOnTestMutation.mutate(testTargets)}
                  disabled={updateOnTestMutation.isPending}
                  title={
                    `Re-publish the edited content to ${
                      testTargets.length > 1
                        ? `${testTargets.length} test site(s)`
                        : testTargets[0].target_name + ' (test)'
                    }, overwriting the current file. Use Promote afterward to push to main.`
                  }
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg disabled:opacity-50 transition-all shadow-sm"
                >
                  <RefreshCw className={`h-4 w-4 ${updateOnTestMutation.isPending ? 'animate-spin' : ''}`} />
                  {updateOnTestMutation.isPending ? 'Updating…' : label}
                </button>
              );
            })()}

            {/* Primary action — one button, state-driven */}
            {article.publish_status === 'draft' && (
              <button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-success-600 hover:bg-success-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-all shadow-sm">
                <CheckCircle className="h-4 w-4" />
                {approveMutation.isPending ? 'Approving...' : 'Approve'}
              </button>
            )}
            {overrideNotPublished && article.publish_status === 'approved' && (
              <span className="text-xs text-warning-800 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2 max-w-md">
                Override approved, but not published: {overrideNotPublished.replace(/_/g, ' ')}. Use Publish to send it live.
              </span>
            )}
            {(article.publish_status === 'approved' || article.publish_status === 'publish_failed') && (
              <button onClick={() => setShowPublishDialog(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all shadow-sm">
                <Send className="h-4 w-4" />
                {article.publish_status === 'publish_failed' ? 'Retry Publish' : 'Publish'}
              </button>
            )}
            {article.publish_status === 'expired' && (
              <>
                <span className="text-xs text-warning-800 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2 max-w-md">
                  Expired unreviewed{article.article_metadata?.expiry?.at
                    ? ` on ${format(new Date(article.article_metadata.expiry.at), 'd MMM yyyy')}`
                    : ''}. Content and Quality Gate judgement are kept, read-only. It will not publish.
                </span>
                <button
                  onClick={() => unexpireMutation.mutate()}
                  disabled={unexpireMutation.isPending}
                  title="Return this article to the review queue (audited)"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-warning-600 text-warning-700 hover:bg-warning-50 font-semibold rounded-lg disabled:opacity-50 transition-all">
                  <RefreshCw className="h-4 w-4" />
                  {unexpireMutation.isPending ? 'Unexpiring…' : 'Unexpire'}
                </button>
              </>
            )}
            {article.publish_status === 'review_blocked' && (
              <button
                onClick={() => setShowOverrideDialog(true)}
                disabled={overrideMutation.isPending}
                title="Approve this article despite the Quality Gate verdict (audited)"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-warning-600 hover:bg-warning-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-all shadow-sm">
                <AlertTriangle className="h-4 w-4" />
                {overrideMutation.isPending ? 'Overriding…' : 'Override review block'}
              </button>
            )}
            {article.publish_status === 'published' && publishTargets && publishTargets.length > 0 && (
              <button
                onClick={() => promoteMutation.mutate({})}
                disabled={promoteMutation.isPending || promoteMutation.isSuccess}
                title={
                  publishTargets.length > 1
                    ? `Snapshot-promote this article to all ${publishTargets.length} sites where it's published on test`
                    : `Snapshot-promote this article to ${publishTargets[0].target_name}`
                }
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-all shadow-sm">
                <Globe className="h-4 w-4" />
                {promoteMutation.isPending
                  ? 'Promoting...'
                  : promoteMutation.isSuccess
                    ? 'Promoted'
                    : publishTargets.length > 1
                      ? `Promote (${publishTargets.length} sites)`
                      : 'Promote'}
              </button>
            )}

            {/* More dropdown — secondary actions */}
            {article.publish_status !== 'archived' && (
              <div className="relative">
                <button onClick={() => setShowUnpublishPicker(!showUnpublishPicker)}
                  className="inline-flex items-center gap-1 px-3 py-2.5 border-2 border-grey-300 text-grey-600 font-medium rounded-lg hover:bg-grey-50 transition-all">
                  More <ChevronDown className="h-3.5 w-3.5" />
                </button>
                {showUnpublishPicker && (
                  <div ref={unpublishPickerRef} className="absolute right-0 top-full mt-2 bg-surface border border-grey-200 rounded-lg shadow-lg z-20 min-w-[200px] max-w-[calc(100vw-2rem)] py-1">
                    {pdfData?.pdf_url && (
                      <a href={pdfData.pdf_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-grey-700 hover:bg-grey-50">
                        <ExternalLink className="h-4 w-4" /> View PDF
                      </a>
                    )}
                    <Link to={`/announcements/${article.announcement_id}`}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-grey-700 hover:bg-grey-50">
                      <FileText className="h-4 w-4" /> Source Announcement
                    </Link>
                    <button onClick={() => { setShowRegenerateDialog(true); setShowUnpublishPicker(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-grey-700 hover:bg-grey-50">
                      <RefreshCw className="h-4 w-4" /> Regenerate
                    </button>
                    <button onClick={() => { setShowCorrectionDialog(true); setShowUnpublishPicker(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-grey-700 hover:bg-grey-50">
                      <FileText className="h-4 w-4" /> Add correction note
                    </button>
                    {/*
                      Re-select / undo / redo USED to live here, in the
                      dropdown gated by `showUnpublishPicker` — the unpublish
                      target picker. An operator edited images in the images
                      panel and rolled them back from a menu named for
                      something else, and all three applied immediately with a
                      republish (§5.2c).

                      They now live in the images panel, beside the images they
                      change, and they STAGE rather than apply: preview first,
                      then one commit. Deliberately not duplicated here — two
                      paths to the same edit is how the immediate and staged
                      versions would drift apart.
                    */}
                    {article.publish_status === 'published' && (
                      <button onClick={() => { setShowPublishDialog(true); setShowUnpublishPicker(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-grey-700 hover:bg-grey-50">
                        <Send className="h-4 w-4" /> Publish to New Target
                      </button>
                    )}
                    {article.publish_status === 'published' && publishTargets?.some((t: any) => t.destination_type === 'main_branch') && (
                      <Link
                        to={`/social?articleId=${article.article_id}`}
                        onClick={() => setShowUnpublishPicker(false)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-grey-700 hover:bg-grey-50"
                      >
                        <Share2 className="h-4 w-4" /> Share to social
                      </Link>
                    )}
                    {/* WP-14: unpublish is per target (all its copies, live
                        first) or everywhere — never per destination, so a
                        test-only unpublish can no longer leave main serving. */}
                    {article.publish_status === 'published' && (() => {
                      const targetNames = Array.from(new Set((publishTargets ?? []).map(t => t.target_name)));
                      return (
                        <>
                          {targetNames.length > 1 && (
                            <button key="unpub-everywhere"
                              onClick={() => {
                                if (confirm(`Unpublish from every site (${targetNames.join(', ')})? Live copies come down first and the published URL is retired.`)) {
                                  unpublishMutation.mutate({});
                                }
                                setShowUnpublishPicker(false);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50">
                              <XCircle className="h-4 w-4" /> Unpublish everywhere
                            </button>
                          )}
                          {targetNames.map(name => (
                            <button key={`unpub-${name}`}
                              onClick={() => {
                                if (confirm(`Unpublish all copies from ${name}? The live copy comes down first.`)) {
                                  unpublishMutation.mutate({ targetName: name });
                                }
                                setShowUnpublishPicker(false);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50">
                              <XCircle className="h-4 w-4" /> Unpublish {name} (all copies)
                            </button>
                          ))}
                        </>
                      );
                    })()}
                    <div className="border-t border-grey-100 mt-1 pt-1">
                      <button onClick={() => { if (confirm('Archive this article?')) archiveMutation?.mutate(); setShowUnpublishPicker(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50">
                        <Archive className="h-4 w-4" /> Archive
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Unpublish Feedback */}
        {unpublishFeedback && (
          <div className={`mt-4 rounded-lg border p-3 text-sm ${
            unpublishFeedback.startsWith('Unpublish failed')
              ? 'bg-danger-50 border-danger-200 text-danger-700'
              : 'bg-success-50 border-success-200 text-success-700'
          }`}>
            <p className="font-semibold">{unpublishFeedback}</p>
          </div>
        )}

        {/* WP-14: social posts that may need manual removal after unpublish.
            Sticky until dismissed — a live post for a pulled article is
            exactly the thing an operator must not miss. */}
        {socialExposure && (
          <div className="mt-4 rounded-lg border p-3 text-sm bg-warning-50 border-warning-200 text-warning-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold">
                  This article has social posts that are not taken down by unpublishing:
                </p>
                <ul className="mt-1.5 space-y-1">
                  {socialExposure.map(p => (
                    <li key={p.post_id} className="flex items-center gap-2 flex-wrap">
                      <span className="capitalize font-medium">{p.platform}</span>
                      <span>
                        {p.exposure === 'posted'
                          ? `— LIVE${p.posted_at ? ` since ${format(new Date(p.posted_at), 'MMM d, HH:mm')}` : ''}`
                          : '— queued in Buffer (remove it there before it posts)'}
                      </span>
                      {p.platform_post_url && (
                        <a href={p.platform_post_url} target="_blank" rel="noopener noreferrer"
                          className="underline text-warning-900">view post</a>
                      )}
                    </li>
                  ))}
                </ul>
                <Link to={`/social?articleId=${id}`} className="mt-1.5 inline-block underline">
                  Open social drafts for this article
                </Link>
              </div>
              <button onClick={() => setSocialExposure(null)}
                className="shrink-0 text-warning-700 hover:text-warning-900 text-xs font-semibold">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Promote Feedback */}
        {promoteFeedback && (
          <div className={`mt-4 rounded-lg border p-3 text-sm ${
            promoteFeedback.type === 'error'
              ? 'bg-danger-50 border-danger-200 text-danger-700'
              : 'bg-primary-50 border-primary-200 text-primary-700'
          }`}>
            <p className="font-semibold">{promoteFeedback.message}</p>
            {promoteFeedback.prUrl && (
              <a
                href={promoteFeedback.prUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-1 text-primary-600 hover:text-primary-800 font-medium underline"
              >
                <ExternalLink className="h-3 w-3" />
                View PR on GitHub
              </a>
            )}
          </div>
        )}

        {/* Publishing Status (real-time via WebSocket) */}
        {publishingStatus && (
          <div className={`mt-4 rounded-lg border p-3 text-sm flex items-center gap-2 ${
            publishingStatus.type === 'publishing' || publishingStatus.type === 'unpublishing'
              ? 'bg-primary-50 border-primary-200 text-primary-700'
              : publishingStatus.type === 'published' || publishingStatus.type === 'unpublished'
              ? 'bg-success-50 border-success-200 text-success-700'
              : 'bg-danger-50 border-danger-200 text-danger-700'
          }`}>
            {(publishingStatus.type === 'publishing' || publishingStatus.type === 'unpublishing') && (
              <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
            )}
            {(publishingStatus.type === 'published' || publishingStatus.type === 'unpublished') && (
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
            )}
            {publishingStatus.type === 'failed' && (
              <XCircle className="h-4 w-4 flex-shrink-0" />
            )}
            <p className="font-semibold">
              {publishingStatus.type === 'publishing' && `Publishing to ${publishingStatus.target}...`}
              {publishingStatus.type === 'unpublishing' && `Unpublishing from ${publishingStatus.target}...`}
              {publishingStatus.type === 'published' && `Published to ${publishingStatus.target}`}
              {publishingStatus.type === 'unpublished' && `Unpublished from ${publishingStatus.target}`}
              {publishingStatus.type === 'failed' && `Publish failed: ${publishingStatus.message || publishingStatus.target}`}
            </p>
            {publishingStatus.type === 'published' && publishingStatus.url && (
              <a
                href={publishingStatus.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 ml-auto text-success-600 hover:text-success-800 font-medium"
              >
                <ExternalLink className="h-3 w-3" />
                View
              </a>
            )}
          </div>
        )}
      </div>

      {/* Source bundle + rewrite history — what fed the article. */}
      {sourceBundle && (
        <SourcesUsedPanel
          articleId={id!}
          data={sourceBundle}
          onOverridden={() => queryClient.invalidateQueries({ queryKey: ['article-sources', id] })}
        />
      )}

      {/* Referenced-periods panel — only renders when this announcement
          scoped a regulatory or trading window (mig 118 referenced_periods
          rows). Surfaces the merit decision, back-source status, and
          chart count so operators can see why the article carries (or
          doesn't carry) a Trading context section. */}
      {referencedPeriods && referencedPeriods.length > 0 && (
        <ReferencedPeriodsPanel periods={referencedPeriods} />
      )}

      {/* SA-SHADOW (2026-10-07): the operator's Release / Hold on a story
          synthesis piece — recorded only, never publishes. */}
      {(article.article_metadata as Record<string, unknown> | null | undefined)?.synthesis_kind === 'story_synthesis' && id && (
        <SynthesisReleasePanel
          articleId={id}
          metadata={article.article_metadata as Record<string, unknown> | null | undefined}
        />
      )}

      {/* Quality Gate Panel — verdict, issues, devil's-advocate notes,
          stale-after-edit banner, override audit footer. Renders whenever
          quality_gate_metadata is present (PASS / PASS WITH NOTES /
          NEEDS REVISION / GATE_ERROR all benefit from operator visibility). */}
      {article.quality_gate_metadata && (
        <QualityGatePanel
          metadata={article.quality_gate_metadata}
          overrideBy={article.quality_gate_override_by}
          overrideReason={article.quality_gate_override_reason}
          overriddenAt={article.quality_gate_overridden_at}
          onPreviewBatch={(indexes, optionKinds) => previewFixBatchMutation.mutate({ indexes, optionKinds })}
          previewPending={previewFixBatchMutation.isPending}
          articleId={id}
          onIdentityRuled={() => queryClient.invalidateQueries({ queryKey: ['article', id] })}
          publishStatus={article.publish_status}
          isSynthesis={article.is_synthesis}
          updatedAt={article.updated_at}
          onFindingResolved={() => {
            queryClient.invalidateQueries({ queryKey: ['article', id] });
            queryClient.invalidateQueries({ queryKey: ['articles'] });
          }}
        />
      )}

      {/* Selective fix-batch review — commit persists + records history,
          discard drops the preview (nothing was saved). */}
      {fixBatch && article && (
        <QgFixBatchModal
          issues={fixBatch.indexes.map((i) => ({
            ...(article.quality_gate_metadata?.issues?.[i] ?? {
              severity: 'MINOR' as const, source_skill: '?', claim: '', fix: '',
            }),
            index: i,
          }))}
          before={article.article_content ?? ''}
          after={fixBatch.newContent}
          faqBefore={fixBatch.faqBefore}
          faqAfter={fixBatch.newFaq}
          committing={commitFixBatchMutation.isPending}
          onCommit={() => commitFixBatchMutation.mutate()}
          onDiscard={() => setFixBatch(null)}
        />
      )}

      {/* Content history + rollback (mig 237) */}
      <ContentHistoryPanel articleId={id!} articleUpdatedAt={article.updated_at ?? null} />


      {/* Article Content */}
      <div className="bg-surface rounded-xl border border-grey-200 overflow-hidden shadow-md">
        {/* Article Header */}
        <div className="px-4 py-5 sm:px-10 sm:py-8 border-b border-grey-200 bg-grey-50">
          {isEditingTitle ? (
            <div className="mb-6">
              <textarea
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                rows={2}
                maxLength={300}
                autoFocus
                className="w-full text-2xl sm:text-4xl font-bold text-grey-900 leading-tight border border-primary-300 rounded-lg px-3 py-2 resize-y focus:border-primary-500 focus:outline-none"
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => saveTitleMutation.mutate()}
                  disabled={
                    saveTitleMutation.isPending
                    || titleDraft.trim() === ''
                    || titleDraft.trim() === article.article_title
                  }
                  className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {saveTitleMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save title
                </button>
                <button
                  onClick={() => setIsEditingTitle(false)}
                  disabled={saveTitleMutation.isPending}
                  className="px-4 py-1.5 text-sm font-medium text-grey-700 border border-grey-300 rounded hover:border-grey-400 disabled:opacity-50"
                >
                  Discard
                </button>
                <p className="text-xs text-grey-500 ml-2">
                  URL stays the same. After saving, push edits to test and promote to update the live site.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 mb-6 group">
              <h1 className="text-2xl sm:text-4xl font-bold text-grey-900 leading-tight">{article.article_title}</h1>
              <button
                onClick={() => {
                  setTitleDraft(article.article_title || '');
                  setIsEditingTitle(true);
                }}
                className="mt-2 p-1.5 text-grey-400 hover:text-primary-600 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0"
                title="Edit title (the published URL never changes)"
              >
                <Pencil className="h-5 w-5" />
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-6 text-base text-grey-600">
            {article.voice_name && (
              <div className="flex items-center space-x-2">
                <Mic className="h-5 w-5 text-primary-600" />
                <span className="font-semibold">{article.voice_name}</span>
              </div>
            )}
            {article.persona_name && (
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-primary-600" />
                <span className="font-semibold">{article.persona_name}</span>
              </div>
            )}
            {article.sector && (
              <div className="flex items-center space-x-2">
                <Tag className="h-5 w-5 text-primary-600" />
                <span className="font-semibold">{article.sector}</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Hash className="h-5 w-5 text-primary-600" />
              <span className="font-semibold">{article.word_count} words</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-primary-600" />
              <span className="font-semibold">{format(new Date(article.generation_timestamp), 'MMMM d, yyyy HH:mm')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5 text-primary-600" />
              <span className="font-semibold">{article.generated_by}</span>
            </div>
          </div>
        </div>

        {/* Article Body — Edit / View Toggle */}
        <div className="px-4 sm:px-10 pt-4 flex items-center justify-end gap-2">
          {/* Presence chip — visible whether or not THIS operator is in
              edit mode, so they can see other operators are editing. */}
          <PresencePill resourceType="article" resourceId={id!} compact />
          {isEditing ? (
            <>
              <button
                onClick={() => saveContentMutation.mutate()}
                disabled={saveContentMutation.isPending}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saveContentMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                disabled={saveContentMutation.isPending}
                className="px-3 py-1.5 text-sm text-grey-600 hover:text-grey-800 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                setEditContent(article.prepared_content || article.article_content || '');
                setIsEditing(true);
              }}
              className="px-3 py-1.5 text-sm text-grey-500 hover:text-grey-700 border border-grey-200 rounded-lg hover:border-grey-300"
            >
              Edit Content
            </button>
          )}
        </div>
        <div className="px-4 py-4 sm:px-10 sm:py-6">
          {isEditing ? (
            <MarkdownEditor value={editContent} onChange={setEditContent} />
          ) : (
            <ArticleBody
              content={article.article_content}
              preparedContent={article.prepared_content}
              preparedImages={article.prepared_images as any}
              legacyImages={(article.article_metadata as any)?.images}
            />
          )}
        </div>
      </div>

      {/* Surgical image edits — Phase B (IMAGE_SELECTION_FIX_PLAN).
          Lives between the article body preview and Published Targets so
          the operator can see the rendered article above, then act on the
          underlying image rows below. */}
      <ArticleImagesPanel
        articleId={id!}
        expectedUpdatedAt={article.updated_at || null}
        preparedImages={article.prepared_images as any}
        rawImageCount={((article as any).article_metadata?.images?.length as number) || 0}
        imageHistoryHeadId={(article as any).image_history_head_id || null}
        isLiveOnProduction={!!publishTargets?.some((t: any) => t.destination_type === 'main_branch')}
        entityDisplayName={(article as any).entity_display_name || null}
        headlineCardSuppressed={(article.article_metadata as Record<string, unknown> | null | undefined)?.headline_card_suppressed === true}
        disabled={article.publish_status === 'archived'}
      />

      {/* Published Targets */}
      {publishTargets && publishTargets.length > 0 && (
        <div className="bg-surface rounded-xl border border-grey-200 p-4 sm:p-8 shadow-md">
          <div className="flex items-start justify-between mb-6 gap-4">
            <h2 className="text-xl font-bold text-grey-900">Published Targets</h2>

            {/* Set MarketFloor Section — operator action to move an article
                between MF sections. Only shown when the article is published
                to marketfloor. Backend handles the metadata update +
                test republish + promote_article in one chain. */}
            {publishTargets.some(t => t.target_name === 'marketfloor') && (() => {
              const currentSection = (
                (article.article_metadata as Record<string, unknown> | undefined)?.section as string | undefined
              )?.toLowerCase();
              const label = currentSection && (MARKETFLOOR_SECTIONS as readonly string[]).includes(currentSection)
                ? `MF section: ${currentSection}`
                : 'MF section: (auto)';
              return (
                <div className="relative" ref={sectionPickerRef}>
                  <button
                    onClick={() => setShowSectionPicker(!showSectionPicker)}
                    disabled={sectionMutation.isPending}
                    title="Move this article between Market Floor sections (companies, deals, markets, economy, policy, people, world, opinion). Triggers a test republish + promote to main."
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 border-2 border-primary-200 rounded-lg hover:bg-primary-50 disabled:opacity-50 transition-colors"
                  >
                    {sectionMutation.isPending ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Globe className="h-3.5 w-3.5" />
                    )}
                    <span>{sectionMutation.isPending ? 'Moving section…' : label}</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  {showSectionPicker && (
                    <div className="absolute right-0 top-full mt-2 bg-surface border border-grey-200 rounded-lg shadow-lg z-20 min-w-[180px] max-w-[calc(100vw-2rem)] py-1">
                      {MARKETFLOOR_SECTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => sectionMutation.mutate(s)}
                          disabled={sectionMutation.isPending || s === currentSection}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            s === currentSection
                              ? 'bg-primary-50 text-primary-700 font-semibold cursor-default'
                              : 'text-grey-700 hover:bg-grey-50'
                          } disabled:cursor-not-allowed disabled:opacity-60`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="space-y-3">
            {publishTargets.map((target) => {
              const reasons = target.sync_reasons ?? [];
              const showSyncFlag = target.needs_sync || reasons.length > 0;
              return (
              <div
                key={`${target.target_id}-${target.destination_name}`}
                className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 border border-grey-200 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <Globe className="h-5 w-5 text-primary-600" />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-grey-900">{target.target_name}</span>
                      {target.destination_name && (
                        <span className="text-sm text-grey-500">/ {target.destination_name}</span>
                      )}
                      {showSyncFlag && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-warning-100 text-warning-700 border border-warning-200">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Needs Sync
                        </span>
                      )}
                    </div>
                    {target.last_published_at && (
                      <span className="text-xs text-grey-500">
                        Published {format(new Date(target.last_published_at), 'MMM d, yyyy HH:mm')}
                      </span>
                    )}
                    {reasons.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {reasons.map((r, i) => (
                          <li key={i} className="text-xs text-warning-700">
                            • {formatSyncReason(r.reason_code, r.reason_detail)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {target.external_url && (
                    <a
                      href={target.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 border border-grey-200 rounded-lg hover:bg-grey-50 transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      <span>View</span>
                    </a>
                  )}
                  {showSyncFlag && (
                    <button
                      onClick={() => republishMutation.mutate({
                        targetName: target.target_name,
                        destinationName: target.destination_name,
                      })}
                      disabled={republishMutation.isPending}
                      className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm text-success-700 border border-success-200 rounded-lg hover:bg-success-50 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${republishMutation.isPending ? 'animate-spin' : ''}`} />
                      <span>Republish</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (confirm(`Unpublish all copies from ${target.target_name}? The live copy comes down first.`)) {
                        unpublishMutation.mutate({ targetName: target.target_name });
                      }
                    }}
                    disabled={unpublishMutation.isPending}
                    className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm text-danger-600 border border-grey-200 rounded-lg hover:bg-danger-50 hover:border-danger-200 transition-colors disabled:opacity-50"
                    title="Removes this target's live AND test copies (WP-14)"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    <span>Unpublish (all copies)</span>
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Publishing History */}
      {publishHistory && publishHistory.length > 0 && (
        <div className="bg-surface rounded-xl border border-grey-200 p-4 sm:p-8 shadow-md">
          <h2 className="text-xl font-bold text-grey-900 mb-6">Publishing History</h2>
          <div className="space-y-3">
            {publishHistory.map((entry) => (
              <div
                key={entry.publish_id}
                className="flex items-start space-x-3 p-3 border border-grey-100 rounded-lg"
              >
                <div className="mt-0.5">
                  {entry.status === 'success' ? (
                    <CheckCircle className="h-5 w-5 text-success-600" />
                  ) : entry.status === 'failed' ? (
                    <XCircle className="h-5 w-5 text-danger-600" />
                  ) : entry.status === 'unpublished' ? (
                    <AlertCircle className="h-5 w-5 text-grey-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-grey-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-grey-900">{entry.target_name}</span>
                    {entry.destination_name && (
                      <span className="text-sm text-grey-500">/ {entry.destination_name}</span>
                    )}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        entry.status === 'success'
                          ? 'bg-success-100 text-success-700'
                          : entry.status === 'failed'
                          ? 'bg-danger-100 text-danger-700'
                          : 'bg-grey-100 text-grey-600'
                      }`}
                    >
                      {entry.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 mt-1 text-xs text-grey-500">
                    {entry.published_at && (
                      <span>{format(new Date(entry.published_at), 'MMM d, yyyy HH:mm')}</span>
                    )}
                    {entry.external_url && (
                      <a
                        href={entry.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        View
                      </a>
                    )}
                  </div>
                  {entry.error_message && (
                    <p className="mt-1 text-xs text-danger-600">{entry.error_message}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Articles */}
      {relationships && relationships.length > 0 && (
        <div className="bg-surface rounded-xl border border-grey-200 p-4 sm:p-8 shadow-md">
          <h2 className="text-xl font-bold text-grey-900 mb-6">Related Articles</h2>
          <div className="space-y-4">
            {['follows', 'cross_target', 'references', 'synthesises'].map((type) => {
              const rels = relationships.filter((r) => r.relationship_type === type);
              if (rels.length === 0) return null;
              const typeLabels: Record<string, string> = {
                follows: 'Continues Story From',
                cross_target: 'Cross-Target Coverage',
                references: 'References',
                synthesises: 'Synthesised From',
              };
              return (
                <div key={type}>
                  <h3 className="text-sm font-semibold text-grey-500 uppercase tracking-wide mb-2">
                    {typeLabels[type] || type}
                  </h3>
                  <div className="space-y-2">
                    {rels.map((rel) => (
                      <div
                        key={rel.relationship_id}
                        className="flex items-center justify-between p-3 border border-grey-100 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <Link
                            to={`/articles/${rel.related_article_id}`}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                          >
                            {rel.related_title || 'Untitled'}
                          </Link>
                          {rel.related_target_name && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-grey-100 text-grey-600">
                              {rel.related_target_name}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={async () => {
                            await deleteArticleRelationship(rel.relationship_id);
                            queryClient.invalidateQueries({ queryKey: ['article-relationships', id] });
                          }}
                          className="text-grey-400 hover:text-danger-500 text-sm"
                          title="Remove relationship"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* External References (from cross-linking) */}
      {article.article_metadata?.external_references?.length > 0 && (
        <div className="bg-surface rounded-xl border border-grey-200 p-6 shadow-md">
          <h2 className="text-lg font-bold text-grey-900 mb-3">External References</h2>
          <div className="space-y-2">
            {article.article_metadata.external_references.map((ref: any, i: number) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-grey-100 last:border-0">
                <ExternalLink className="h-4 w-4 text-grey-400 flex-shrink-0" />
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:underline truncate"
                >
                  {ref.title || ref.url}
                </a>
                {ref.source && (
                  <span className="text-xs text-grey-400 flex-shrink-0">{ref.source}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata Card */}
      <div className="bg-surface rounded-xl border border-grey-200 p-4 sm:p-8 shadow-md">
        <h2 className="text-xl font-bold text-grey-900 mb-6">Article Metadata</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(article.entity_display_name || article.company_code) && (
            <div className="flex items-start space-x-3">
              <div className="text-grey-400 mt-0.5"><TrendingUp className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className="text-sm text-grey-500 font-medium mb-1">Entity</p>
                <p className="text-base font-semibold text-grey-900 break-all">
                  {article.entity_display_name || '—'}
                  {article.entity_canonical_identifier ? ` (${article.entity_canonical_identifier})` : ''}
                </p>
                {article.company_code && (
                  <p className="text-xs text-grey-500 mt-1">
                    Published badge:{' '}
                    <span className="font-mono font-semibold text-grey-700">
                      {article.exchange_code || 'ASX'}: {article.company_code}
                    </span>
                  </p>
                )}
                {article.announcement_id && (
                  <button
                    onClick={() => setShowCorrectCompanyDialog(true)}
                    className="mt-2 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    Correct company / ticker…
                  </button>
                )}
              </div>
            </div>
          )}
          {article.writing_style_name && (
            <MetadataItem
              icon={<FileText className="h-5 w-5" />}
              label="Writing Style"
              value={article.writing_style_name}
            />
          )}
          <MetadataItem
            icon={<User className="h-5 w-5" />}
            label="AI Model"
            value={article.generated_by}
          />
          {article.temperature !== null && article.temperature !== undefined && (
            <MetadataItem
              icon={<Hash className="h-5 w-5" />}
              label="Temperature"
              value={article.temperature.toString()}
            />
          )}
          {article.generation_request_id && (
            <MetadataItem
              icon={<FileText className="h-5 w-5" />}
              label="Request ID"
              value={article.generation_request_id}
            />
          )}
        </div>

        {/* Fact-Check Verification */}
        {article.fact_check_metadata && (() => {
          const fcm = typeof article.fact_check_metadata === 'string'
            ? JSON.parse(article.fact_check_metadata)
            : article.fact_check_metadata;
          const status = fcm?.verification_status;
          const verified = fcm?.claims_verified || [];
          const remaining = fcm?.flags_remaining || [];
          const errors = fcm?.verification_errors || [];
          const footnote = fcm?.verification_footnote;
          const hasIssues = remaining.length > 0 || errors.length > 0;

          if (status === 'none' && !hasIssues) return null;

          return (
            <div className="mt-8 pt-8 border-t border-grey-200">
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className={`h-5 w-5 ${hasIssues ? 'text-warning-600' : 'text-success-600'}`} />
                <h3 className="text-lg font-bold text-grey-900">Data Verification</h3>
                {hasIssues && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-warning-100 text-warning-700 border border-warning-200">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {remaining.length} unresolved
                  </span>
                )}
                {status === 'complete' && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-success-100 text-success-700 border border-success-200">
                    All verified
                  </span>
                )}
              </div>

              {/* Verified claims */}
              {footnote?.claims_by_type && Object.keys(footnote.claims_by_type).length > 0 && (
                <div className="mb-4 p-4 bg-success-50 rounded-lg border border-success-200">
                  <p className="text-sm font-semibold text-success-800 mb-2">
                    Verified ({footnote.total_verified} claim{footnote.total_verified !== 1 ? 's' : ''}) via {footnote.source}
                  </p>
                  {Object.entries(footnote.claims_by_type).map(([type, claims]: [string, any]) => (
                    <div key={type} className="mt-2">
                      <p className="text-xs font-semibold text-success-700">{type}</p>
                      {claims.map((claim: any, i: number) => (
                        <p key={i} className="text-xs text-success-600 ml-3">
                          {claim.original} ({claim.confidence} confidence)
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Unresolved flags */}
              {remaining.length > 0 && (
                <div className="mb-4 p-4 bg-warning-50 rounded-lg border border-warning-200">
                  <p className="text-sm font-semibold text-warning-800 mb-2">
                    Unresolved Data Points ({remaining.length})
                  </p>
                  <p className="text-xs text-warning-600 mb-2">
                    These placeholders could not be verified and were stripped from published content.
                  </p>
                  {remaining.map((flag: string, i: number) => (
                    <p key={i} className="text-xs text-warning-700 ml-3 mb-1 font-mono">
                      {flag}
                    </p>
                  ))}
                </div>
              )}

              {/* Errors */}
              {errors.length > 0 && (
                <div className="p-4 bg-danger-50 rounded-lg border border-danger-200">
                  <p className="text-sm font-semibold text-danger-800 mb-2">
                    Verification Errors ({errors.length})
                  </p>
                  {errors.map((err: any, i: number) => (
                    <p key={i} className="text-xs text-danger-600 ml-3 mb-1">
                      {err.claim ? `${err.claim}: ` : ''}{err.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {article.edited && (
          <div className="mt-8 pt-8 border-t border-grey-200">
            <h3 className="text-lg font-bold text-grey-900 mb-6">Edit History</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {article.edited_by && (
                <MetadataItem
                  icon={<User className="h-5 w-5" />}
                  label="Edited By"
                  value={article.edited_by}
                />
              )}
              {article.edited_at && (
                <MetadataItem
                  icon={<Calendar className="h-5 w-5" />}
                  label="Edited At"
                  value={format(new Date(article.edited_at), 'MMMM d, yyyy HH:mm')}
                />
              )}
            </div>
            {article.edit_notes && (
              <div className="mt-6 p-5 bg-grey-50 rounded-lg border border-grey-200">
                <p className="text-sm font-semibold text-grey-700 mb-2">Edit Notes</p>
                <p className="text-sm text-grey-600 leading-relaxed">{article.edit_notes}</p>
              </div>
            )}
          </div>
        )}

        {article.approved_by && (
          <div className="mt-8 pt-8 border-t border-grey-200">
            <h3 className="text-lg font-bold text-grey-900 mb-6">Approval</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MetadataItem
                icon={<User className="h-5 w-5" />}
                label="Approved By"
                value={article.approved_by}
              />
              {article.approved_at && (
                <MetadataItem
                  icon={<Calendar className="h-5 w-5" />}
                  label="Approved At"
                  value={format(new Date(article.approved_at), 'MMMM d, yyyy HH:mm')}
                />
              )}
            </div>
          </div>
        )}

        {article.published_at && (
          <div className="mt-8 pt-8 border-t border-grey-200">
            <h3 className="text-lg font-bold text-grey-900 mb-6">Publication</h3>
            <MetadataItem
              icon={<Calendar className="h-5 w-5" />}
              label="Published At"
              value={format(new Date(article.published_at), 'MMMM d, yyyy HH:mm')}
            />
          </div>
        )}

        <div className="mt-8 pt-8 border-t border-grey-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-grey-500">
            <div>
              <span className="font-semibold">Created:</span>{' '}
              {format(new Date(article.created_at), 'MMM d, yyyy HH:mm:ss')}
            </div>
            <div>
              <span className="font-semibold">Updated:</span>{' '}
              {format(new Date(article.updated_at), 'MMM d, yyyy HH:mm:ss')}
            </div>
          </div>
        </div>
      </div>

      {/* Public correction dialog — appends to the article's change
          history (foot-of-article disclosure + CorrectionComment JSON-LD)
          and republishes. Append-only by design. */}
      {showCorrectionDialog && (
      <Modal onClose={() => setShowCorrectionDialog(false)} canClose={!correctionSaving} closeOnBackdrop>
        <ModalHeader>
          <h3 className="text-base font-semibold text-grey-900">Add public correction note</h3>
          <button onClick={() => setShowCorrectionDialog(false)} aria-label="Close" disabled={correctionSaving} className="touch-target">
            <X className="h-5 w-5 text-grey-500 hover:text-grey-700" />
          </button>
        </ModalHeader>
        <ModalBody>
          <p className="text-xs text-grey-500 mb-2">
            Published at the foot of the article with today's date. The original
            publish date and sort order never change. Write the note for readers:
            state what was corrected and the right fact.
          </p>
          <textarea
            value={correctionNote}
            onChange={(e) => setCorrectionNote(e.target.value)}
            rows={4}
            placeholder="Corrected the stratigraphic boundary: fresh basement begins below 488.3 m, not 455 m."
            className="w-full rounded border border-grey-300 bg-surface p-2 text-sm"
          />
          <div className="flex gap-4 mt-3 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={correctionKind === 'correction'} onChange={() => setCorrectionKind('correction')} />
              Correction (factual fix)
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={correctionKind === 'update'} onChange={() => setCorrectionKind('update')} />
              Update (new information)
            </label>
          </div>
          {Array.isArray((article.article_metadata as any)?.change_history) && (article.article_metadata as any).change_history.length > 0 && (
            <div className="mt-4 border-t border-grey-100 pt-3">
              <p className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-1.5">Existing history</p>
              <ul className="space-y-1 text-xs text-grey-600">
                {((article.article_metadata as any).change_history as Array<{ at: string; note: string; kind: string }>).map((c, i) => (
                  <li key={i}><span className="font-mono">{(c.at || '').slice(0, 10)}</span>: {c.note}{c.kind === 'update' ? ' (update)' : ''}</li>
                ))}
              </ul>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            onClick={() => setShowCorrectionDialog(false)}
            disabled={correctionSaving}
            className="touch-target px-4 py-2 text-sm rounded border border-grey-300 text-grey-600 hover:bg-grey-50 disabled:opacity-50"
          >Discard</button>
          <button
            disabled={correctionSaving || !correctionNote.trim()}
            onClick={async () => {
              setCorrectionSaving(true);
              try {
                await addArticleCorrection(article.article_id, { note: correctionNote.trim(), kind: correctionKind });
                setCorrectionNote('');
                setShowCorrectionDialog(false);
                queryClient.invalidateQueries({ queryKey: ['article', article.article_id] });
              } catch (e: any) {
                alert(`Correction failed: ${e?.response?.data?.detail || e?.message || 'unknown error'}`);
              } finally {
                setCorrectionSaving(false);
              }
            }}
            className="touch-target px-4 py-2 text-sm rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
          >{correctionSaving ? 'Publishing…' : 'Publish correction'}</button>
        </ModalFooter>
      </Modal>
      )}

      {/* Regenerate Dialog */}
      <RegenerateArticleDialog
        isOpen={showRegenerateDialog}
        onClose={() => setShowRegenerateDialog(false)}
        announcementId={article.announcement_id}
        onSuccess={handleRegenerateSuccess}
        currentArticle={{
          voice_name: article.voice_name,
          persona_slug: article.persona_slug,
          generated_by: article.generated_by,
          intended_target_name: article.intended_target_name,
          intended_destination_name: article.intended_destination_name,
          article_id: article.article_id,
          published_slug: article.published_slug ?? null,
          publish_status: article.publish_status,
        }}
      />

      {/* Publish Dialog */}
      <PublishDialog
        isOpen={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        articleId={article.article_id}
        publishedTargets={publishTargets}
        defaultTargetName={article.intended_target_name}
        onSuccess={(targetName) => {
          setPublishingStatus({ type: 'publishing', target: targetName });
          queryClient.invalidateQueries({ queryKey: ['article', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
        }}
      />

      <CorrectCompanyDialog
        isOpen={showCorrectCompanyDialog}
        onClose={() => setShowCorrectCompanyDialog(false)}
        articleId={article.article_id}
        expectedUpdatedAt={article.updated_at}
        currentCompanyCode={article.company_code}
        currentExchangeCode={article.exchange_code}
        currentEntityName={article.entity_display_name}
        currentEntityTicker={article.entity_canonical_identifier}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['article', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publish-targets', id] });
          queryClient.invalidateQueries({ queryKey: ['article-publishing-history', id] });
        }}
      />

      {/* Quality Gate Override Dialog */}
      <QualityGateOverrideDialog
        isOpen={showOverrideDialog}
        onClose={() => setShowOverrideDialog(false)}
        metadata={article.quality_gate_metadata}
        onSubmit={async (reason) => {
          await overrideMutation.mutateAsync(reason);
        }}
      />
    </div>
  );
}

function MetadataItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start space-x-3">
      <div className="text-grey-400 mt-0.5">{icon}</div>
      <div>
        <p className="text-sm text-grey-500 font-medium mb-1">{label}</p>
        <p className="text-base font-semibold text-grey-900 break-all">{value}</p>
      </div>
    </div>
  );
}

// ============================================================================
// Rewrite-review panel — Step 15 cap (mig 114)
// ============================================================================

function RewriteReviewPanel({
  articleId,
  data,
  onDecided,
}: {
  articleId: string;
  data: { rewrite_count: number; operator_edited: boolean; decisions: RewriteDecision[] };
  onDecided: () => void;
}) {
  const [reasonInput, setReasonInput] = useState('');
  const [busyDecisionId, setBusyDecisionId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const pending = data.decisions.find((d) => d.decision_state === 'pending') || null;
  const recentResolved = data.decisions
    .filter((d) => d.decision_state !== 'pending')
    .slice(0, 3);

  const onApprove = async (decisionId: string) => {
    setErrorMsg(null);
    setBusyDecisionId(decisionId);
    try {
      await approveRewriteDecision(articleId, decisionId, reasonInput || undefined);
      setReasonInput('');
      onDecided();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || err?.message || 'Approve failed');
    } finally {
      setBusyDecisionId(null);
    }
  };

  const onReject = async (decisionId: string) => {
    setErrorMsg(null);
    setBusyDecisionId(decisionId);
    try {
      await rejectRewriteDecision(articleId, decisionId, reasonInput || undefined);
      setReasonInput('');
      onDecided();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || err?.message || 'Reject failed');
    } finally {
      setBusyDecisionId(null);
    }
  };

  if (!pending && recentResolved.length === 0 && data.rewrite_count === 0) {
    return null;
  }

  return (
    <div
      className={`rounded-xl border shadow-md p-6 ${
        pending
          ? 'bg-warning-50 border-warning-300'
          : 'bg-grey-50 border-grey-200'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {pending ? (
            <AlertTriangle className="h-5 w-5 text-warning-700 mt-0.5 flex-shrink-0" />
          ) : (
            <RefreshCw className="h-5 w-5 text-grey-500 mt-0.5 flex-shrink-0" />
          )}
          <div>
            <h3 className="text-base font-bold text-grey-900">
              {pending ? 'Rewrite proposed (review required)' : 'Rewrite history'}
            </h3>
            <p className="text-sm text-grey-600 mt-0.5">
              {pending ? (
                <>
                  This article has been rewritten {data.rewrite_count} times today.
                  {data.operator_edited && ' It also carries operator edits.'} The
                  planner is proposing another rewrite — approve to run it, or reject
                  to keep the article as-is.
                </>
              ) : (
                <>
                  Rewritten {data.rewrite_count} {data.rewrite_count === 1 ? 'time' : 'times'} today.
                  {data.operator_edited && ' Operator edits applied.'}
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {pending && (
        <div className="mt-4 space-y-3 border-t border-warning-200 pt-4">
          <div>
            <p className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-1">
              Triage rationale
            </p>
            <p className="text-sm text-grey-800">
              {pending.triage_rationale || '(no rationale recorded)'}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-1">
              Proposed source bundle
            </p>
            <ul className="text-sm text-grey-800 space-y-1">
              {pending.proposed_source_bundle.primary_announcement_id && (
                <li className="font-mono text-xs">
                  primary: {pending.proposed_source_bundle.primary_announcement_id}
                </li>
              )}
              {(pending.proposed_source_bundle.source_announcement_ids || []).map((sid) => (
                <li key={sid} className="font-mono text-xs text-grey-500">
                  source: {sid}
                </li>
              ))}
              {pending.proposed_source_bundle.action && (
                <li className="text-xs text-grey-600">
                  action: <strong>{pending.proposed_source_bundle.action}</strong>
                </li>
              )}
              {pending.proposed_source_bundle.triggering_announcement_id && (
                <li className="text-xs text-grey-600">
                  triggered by:{' '}
                  <span className="font-mono">
                    {pending.proposed_source_bundle.triggering_announcement_id}
                  </span>
                </li>
              )}
            </ul>
          </div>

          {errorMsg && (
            <div className="bg-danger-50 border border-danger-200 rounded p-2 text-sm text-danger-700">
              {errorMsg}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-grey-500 uppercase tracking-wide mb-1">
              Reason (optional)
            </label>
            <textarea
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              rows={2}
              placeholder="Operator reason — recorded with the decision for audit"
              className="w-full px-3 py-2 text-sm border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onApprove(pending.decision_id)}
              disabled={busyDecisionId === pending.decision_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-success-600 hover:bg-success-700 disabled:opacity-60 text-white font-semibold rounded-lg transition-colors"
            >
              {busyDecisionId === pending.decision_id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              Approve rewrite
            </button>
            <button
              onClick={() => onReject(pending.decision_id)}
              disabled={busyDecisionId === pending.decision_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-danger-50 hover:bg-danger-100 disabled:opacity-60 text-danger-700 font-semibold rounded-lg border border-danger-300 transition-colors"
            >
              <XCircle className="h-4 w-4" />
              Reject
            </button>
          </div>
        </div>
      )}

      {recentResolved.length > 0 && (
        <details className="mt-4 border-t border-grey-200 pt-3">
          <summary className="text-xs font-semibold text-grey-500 uppercase tracking-wide cursor-pointer hover:text-grey-700">
            Recent decisions ({recentResolved.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {recentResolved.map((d) => (
              <li key={d.decision_id} className="text-xs text-grey-600 flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded font-semibold ${
                    d.decision_state === 'approved'
                      ? 'bg-success-100 text-success-700'
                      : d.decision_state === 'rejected'
                      ? 'bg-danger-100 text-danger-700'
                      : 'bg-grey-100 text-grey-600'
                  }`}
                >
                  {d.decision_state}
                </span>
                <span className="text-grey-400">
                  {format(new Date(d.decided_at || d.created_at), 'HH:mm:ss')}
                </span>
                {d.operator_email && <span className="text-grey-500">by {d.operator_email}</span>}
                {d.operator_reason && <span className="italic">"{d.operator_reason}"</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ============================================================================
// Sources-used panel — Phase 2 visibility
// ============================================================================

function SourcesUsedPanel({
  articleId,
  data,
  onOverridden,
}: {
  articleId: string;
  data: ArticleSourcesResponse;
  onOverridden?: () => void;
}) {
  if (!data.plan_item || data.sources.length === 0) {
    if (data.rewrite_history.length === 0) return null;
  }

  const item = data.plan_item;
  const anchor = item
    ? data.sources.find((s) => s.announcement_id === item.primary_announcement_id) || data.sources[0]
    : null;
  const supporting = data.sources.filter(
    (s) => !item || s.announcement_id !== item.primary_announcement_id,
  );

  return (
    <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-6">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-grey-500" />
        <h3 className="text-base font-bold text-grey-900">Sources used</h3>
        {item?.action && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-grey-100 text-grey-600 font-mono">
            {item.action}
          </span>
        )}
        {item?.target_name && (
          <span className="text-xs text-grey-500 ml-auto">→ {item.target_name}</span>
        )}
      </div>
      {item?.triage_rationale && (
        <details className="mb-3">
          <summary className="text-[10px] font-semibold text-grey-500 uppercase cursor-pointer">
            Triage rationale
          </summary>
          <p className="mt-1 text-xs text-grey-700">{item.triage_rationale}</p>
        </details>
      )}
      {data.sources.length === 0 ? (
        <p className="text-sm text-grey-500 italic">
          No source bundle recorded — this article may pre-date the Phase 1 plumbing.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {anchor && (
            <ArticleSourceRow
              s={anchor}
              isAnchor
              articleId={articleId}
              onOverridden={onOverridden}
            />
          )}
          {supporting.map((s) => (
            <ArticleSourceRow
              key={s.announcement_id}
              s={s}
              articleId={articleId}
              onOverridden={onOverridden}
            />
          ))}
        </ul>
      )}

      {data.rewrite_history.length > 0 && (
        <div className="mt-4 border-t border-grey-200 pt-3">
          <p className="text-[10px] font-semibold text-grey-500 uppercase mb-2">
            Rewrite history
          </p>
          <ul className="space-y-1.5 text-xs">
            {data.rewrite_history.map((h) => {
              const ts = h.decided_at || h.created_at;
              return (
                <li key={h.decision_id} className="flex items-start gap-2 text-grey-700">
                  <span
                    className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded font-semibold uppercase ${
                      h.decision_state === 'approved'
                        ? 'bg-success-100 text-success-700'
                        : h.decision_state === 'rejected'
                        ? 'bg-danger-100 text-danger-700'
                        : 'bg-grey-100 text-grey-600'
                    }`}
                  >
                    {h.decision_state}
                  </span>
                  <span className="text-grey-500 font-mono text-[10px]">
                    v{h.rewrite_count_before}→v{h.rewrite_count_before + 1}
                  </span>
                  <span className="text-grey-500">{format(new Date(ts), 'MMM d HH:mm')}</span>
                  {h.operator_email && <span className="text-grey-500">by {h.operator_email}</span>}
                  {h.operator_reason && <span className="italic">"{h.operator_reason}"</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function ArticleSourceRow({
  s,
  isAnchor,
  articleId,
  onOverridden,
}: {
  s: ArticleSource;
  isAnchor?: boolean;
  articleId?: string;
  onOverridden?: () => void;
}) {
  const substance = s.editorial_substance;
  const curatorRole = s.operator_override_role || s.curator_role || null;
  const [busy, setBusy] = useState(false);

  const onSelectRole = async (role: 'anchor' | 'supporting' | 'cite_only' | 'exclude') => {
    if (!articleId) return;
    setBusy(true);
    try {
      await overrideSourceRole(articleId, s.announcement_id, role);
      onOverridden?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex items-start gap-2 text-sm">
      <span
        className={`flex-shrink-0 w-16 inline-flex justify-center items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${
          isAnchor ? 'bg-primary-100 text-primary-700' : 'bg-grey-200 text-grey-700'
        }`}
      >
        {isAnchor ? 'anchor' : s.role || 'source'}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-grey-800">{s.title}</p>
        <p className="text-[10px] text-grey-500 flex flex-wrap gap-2 mt-0.5">
          {s.company_code && <span className="font-mono">{s.company_code}</span>}
          {s.doc_form && <span className="font-mono">{s.doc_form}</span>}
          {substance && (
            <span
              className={`px-1 rounded ${substanceBadgeClass(substance)}`}
              title={SUBSTANCE_LABEL[substance]}
            >
              substance {substance}
            </span>
          )}
          {s.activity_sub_type && <span>{s.activity_sub_type}</span>}
          <span>{format(new Date(s.announcement_date), 'MMM d')}</span>
          {curatorRole && (
            <span
              className={`px-1 rounded font-semibold ${
                s.operator_override_role
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-indigo-50 text-indigo-700'
              }`}
              title={
                s.operator_override_role
                  ? `Operator override (${s.operator_override_by}): ${curatorRole}`
                  : `Curator: ${curatorRole}`
              }
            >
              {s.operator_override_role ? 'operator' : 'curator'}: {curatorRole}
            </span>
          )}
        </p>
        {s.curator_rationale && (
          <p className="text-[10px] text-grey-600 italic mt-0.5">{s.curator_rationale}</p>
        )}
        {articleId && (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[9px] text-grey-400 uppercase tracking-wide">Override</span>
            {(['anchor', 'supporting', 'cite_only', 'exclude'] as const).map((r) => (
              <button
                key={r}
                disabled={busy}
                onClick={() => onSelectRole(r)}
                className={`text-[10px] px-2 py-1.5 sm:px-1.5 sm:py-0.5 rounded border transition-colors ${
                  curatorRole === r
                    ? 'bg-grey-200 border-grey-300 text-grey-700'
                    : 'bg-surface border-grey-200 text-grey-500 hover:text-grey-800 hover:border-grey-400'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}


// ---------------------------------------------------------------------------
// ReferencedPeriodsPanel — operator visibility for mig 118 referenced_periods
// ---------------------------------------------------------------------------

function ReferencedPeriodsPanel({ periods }: { periods: ReferencedPeriod[] }) {
  return (
    <div className="bg-surface rounded-xl border border-grey-200 p-6 shadow-md space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-grey-900">Referenced periods</h3>
        <span className="text-xs text-grey-500">
          {periods.length} window{periods.length === 1 ? '' : 's'} detected
        </span>
      </div>
      <p className="text-sm text-grey-600 leading-snug">
        Regulatory and compliance windows the triggering announcement
        scoped. When a window is enriched, supporting filings + market
        context feed the article body.
      </p>
      <div className="space-y-3">
        {periods.map((p) => (
          <ReferencedPeriodRow key={p.period_id} period={p} />
        ))}
      </div>
    </div>
  );
}

function ReferencedPeriodRow({ period }: { period: ReferencedPeriod }) {
  const subjectLabel = period.subject_type === 'trading'
    ? 'Trading window'
    : 'Announcements window';
  const decisionBadge = period.backfill_decision === 'enrich'
    ? 'bg-blue-100 text-blue-800'
    : period.backfill_decision === 'skip'
    ? 'bg-grey-100 text-grey-700'
    : 'bg-amber-100 text-amber-800';
  const statusBadge = (() => {
    switch (period.back_source_status) {
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-amber-100 text-amber-800';
      case 'in_progress':
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'not_found':
      case 'timed_out':
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'out_of_scope':
        return 'bg-grey-100 text-grey-600';
      default:
        return 'bg-grey-100 text-grey-500';
    }
  })();
  return (
    <div className="rounded-lg border border-grey-200 bg-grey-50 p-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-grey-900">
            {period.ticker} · {subjectLabel}
          </div>
          <div className="text-xs text-grey-600 mt-0.5">
            {period.start_date} → {period.end_date}
            {period.purpose && (
              <span className="ml-2 italic">({period.purpose})</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {period.narrative_centrality && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-grey-200 text-grey-700">
              {period.narrative_centrality}
            </span>
          )}
          {period.backfill_decision && (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${decisionBadge}`}>
              {period.backfill_decision}
            </span>
          )}
          {period.back_source_status && (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge}`}>
              {period.back_source_status}
            </span>
          )}
        </div>
      </div>
      {period.verbatim && (
        <blockquote className="mt-2 text-xs italic text-grey-600 border-l-2 border-grey-300 pl-2">
          "{period.verbatim}"
        </blockquote>
      )}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-grey-700">
        {period.subject_type === 'announcements' && (
          <span>
            <strong>{period.announcements_ingested}</strong> filings ingested
          </span>
        )}
        {period.subject_type === 'trading' && (
          <>
            <span>
              <strong>{period.days_traded}</strong> trading days
            </span>
            <span>
              <strong>{period.zoom_days}</strong> zoom day{period.zoom_days === 1 ? '' : 's'}
            </span>
          </>
        )}
        {period.chart_count > 0 && (
          <span>
            <strong>{period.chart_count}</strong> chart{period.chart_count === 1 ? '' : 's'} rendered
          </span>
        )}
      </div>
      {period.backfill_decision_reason && (
        <div className="mt-2 text-xs text-grey-500 italic">
          {period.backfill_decision_reason}
        </div>
      )}
    </div>
  );
}
